import { useState, useRef } from 'react'
import { Upload, Download, X, Wand2 } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function BackgroundRemover() {
  const [selectedFile, setSelectedFile] = useState(null)
  const [processedImage, setProcessedImage] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [tolerance, setTolerance] = useState(30)
  const fileInputRef = useRef(null)

  const handleFileSelect = (file) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.')
      return
    }
    setSelectedFile(file)
    setProcessedImage(null)
  }

  const removeBackground = () => {
    if (!selectedFile) return

    setIsProcessing(true)
    const img = new Image()
    const reader = new FileReader()

    reader.onload = (e) => {
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')

        ctx.drawImage(img, 0, 0)

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imageData.data

        // Get corner colors (assuming corners are background)
        const corners = [
          { x: 0, y: 0 },
          { x: canvas.width - 1, y: 0 },
          { x: 0, y: canvas.height - 1 },
          { x: canvas.width - 1, y: canvas.height - 1 },
        ]

        const cornerColors = corners.map((corner) => {
          const index = (corner.y * canvas.width + corner.x) * 4
          return {
            r: data[index],
            g: data[index + 1],
            b: data[index + 2],
          }
        })

        // Average corner colors to get background color
        const bgColor = {
          r: Math.round(cornerColors.reduce((sum, c) => sum + c.r, 0) / cornerColors.length),
          g: Math.round(cornerColors.reduce((sum, c) => sum + c.g, 0) / cornerColors.length),
          b: Math.round(cornerColors.reduce((sum, c) => sum + c.b, 0) / cornerColors.length),
        }

        // Remove background pixels
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]
          const g = data[i + 1]
          const b = data[i + 2]

          const distance = Math.sqrt(
            Math.pow(r - bgColor.r, 2) + Math.pow(g - bgColor.g, 2) + Math.pow(b - bgColor.b, 2)
          )

          if (distance < tolerance) {
            data[i + 3] = 0 // Set alpha to 0 (transparent)
          }
        }

        ctx.putImageData(imageData, 0, 0)

        canvas.toBlob(
          (blob) => {
            const url = URL.createObjectURL(blob)
            setProcessedImage({ url, blob })
            setIsProcessing(false)
          },
          'image/png', // PNG supports transparency
          1.0
        )
      }
      img.src = e.target.result
    }

    reader.readAsDataURL(selectedFile)
  }

  const downloadImage = () => {
    if (!processedImage) return

    const link = document.createElement('a')
    link.href = processedImage.url
    link.download = `no-background-${selectedFile.name.replace(/\.[^/.]+$/, '')}.png`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const clearFile = () => {
    setSelectedFile(null)
    setProcessedImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className="space-y-6">
      <Card
        className={`cursor-pointer transition-all ${
          selectedFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary-400'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        <Card.Body className="text-center py-8">
          {!selectedFile ? (
            <div>
              <div className="mb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-full">
                  <Upload className="w-8 h-8 text-indigo-600" />
                </div>
              </div>
              <p className="text-base font-semibold text-gray-900">Click to upload image</p>
              <p className="text-sm text-gray-500 mt-1">Select an image to remove background</p>
              <p className="text-xs text-gray-400 mt-2">
                Note: Works best with images that have a solid or uniform background
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-4">
              <div className="text-left">
                <p className="text-base font-semibold text-gray-900">{selectedFile.name}</p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  clearFile()
                }}
                className="ml-auto p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) handleFileSelect(e.target.files[0])
        }}
      />

      {selectedFile && (
        <>
          <Card>
            <Card.Body>
              <div className="mb-6">
                <label className="label">Sensitivity (Tolerance): {tolerance}</label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={tolerance}
                  onChange={(e) => setTolerance(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Less sensitive (10)</span>
                  <span>More sensitive (100)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Lower values remove only exact background colors. Higher values remove similar colors.
                </p>
              </div>

              <Button
                onClick={removeBackground}
                variant="primary"
                className="w-full"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Remove Background
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>

          {processedImage && (
            <Card>
              <Card.Body>
                <div className="text-center space-y-4">
                  <div className="inline-block p-4 bg-gray-100 rounded-lg" style={{ backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)', backgroundSize: '20px 20px', backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px' }}>
                    <img src={processedImage.url} alt="Background removed" className="max-w-full h-auto max-h-96" />
                  </div>
                  <Button onClick={downloadImage} variant="primary" size="lg" className="w-full">
                    <Download className="w-5 h-5 mr-2" />
                    Download Image (PNG)
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  )
}

